import { useEffect, useRef } from 'react';
import { useCacheSettings } from '../stores/CacheSettingsContext/CacheSettingsContext';

/**
 * Hook for auto-loading component data when persistent cache is enabled
 * @param {Function} loadFunction - Function to call for loading data
 * @param {Array} dependencies - Dependencies to trigger reload (e.g., [activeFile])
 * @param {boolean} hasData - Whether component already has data
 * @returns {object} Auto-load state and controls
 */
export const useAutoLoad = (loadFunction, dependencies = [], hasData = false) => {
  const { isPersistentCacheEnabled, isLoading: cacheSettingsLoading } = useCacheSettings();
  const hasAutoLoaded = useRef(false);
  const previousDependencies = useRef(dependencies);
  const timeoutRef = useRef(null);

  // Check if dependencies have changed
  const dependenciesChanged = !dependencies.every(
    (dep, index) => dep === previousDependencies.current[index]
  );

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Update previous dependencies
    previousDependencies.current = dependencies;

    // Don't auto-load if:
    // - Cache settings are still loading
    // - Persistent cache is not enabled
    // - Component already has data and dependencies haven't changed
    // - We've already auto-loaded for this set of dependencies
    if (
      cacheSettingsLoading ||
      !isPersistentCacheEnabled ||
      (hasData && !dependenciesChanged && hasAutoLoaded.current)
    ) {
      return;
    }

    // Reset auto-load flag if dependencies changed
    if (dependenciesChanged) {
      hasAutoLoaded.current = false;
    }

    // Auto-load if we haven't already
    if (!hasAutoLoaded.current) {
      // Only auto-load if all required dependencies are available
      const hasRequiredDependencies = dependencies.every(dep => dep != null && dep !== undefined);
      
      if (hasRequiredDependencies) {
        console.log('🚀 Auto-loading component data (persistent cache enabled)');
        loadFunction();
        hasAutoLoaded.current = true;
      }
    }
  }, [isPersistentCacheEnabled, cacheSettingsLoading, loadFunction, hasData, dependenciesChanged]);

  // Reset auto-load flag when dependencies change significantly
  useEffect(() => {
    if (dependenciesChanged) {
      hasAutoLoaded.current = false;
    }
  }, dependencies);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    shouldAutoLoad: isPersistentCacheEnabled && !cacheSettingsLoading,
    hasAutoLoaded: hasAutoLoaded.current,
    isPersistentCacheEnabled,
    cacheSettingsLoading
  };
};